// === App.js ===
import React, { useState } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";

function UploadPage() {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState("");
  const [uploadSuccess, setUploadSuccess] = useState(false);

  const [processing, setProcessing] = useState(false);
  const [processProgress, setProcessProgress] = useState(0);
  const [processResult, setProcessResult] = useState(null);
  const [faceInfo, setFaceInfo] = useState(null);

  const [imagePath, setImagePath] = useState("");
  const [imageNames, setImageNames] = useState([]);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalImage, setModalImage] = useState("");
  const [modalInfo, setModalInfo] = useState(null);
  const [modalIndex, setModalIndex] = useState(0);

  const resetStates = () => {
    setProcessProgress(0);
    setProcessResult(null);
    setFaceInfo(null);
    setImagePath("");
    setImageNames([]);
    setStatusMessage("");
    setUploadSuccess(false);
  };

  const handleFileClick = () => {
    setFile(null);
    resetStates();
    document.querySelector("input[type='file']").value = ""; // Clear file input
  };



  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    setFile(selectedFile);
    setUploading(true);
    setUploadProgress(0);

    const formData = new FormData();
    formData.append("video", selectedFile);

    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener("progress", (e) => {
      if (e.lengthComputable) {
        const percent = Math.round((e.loaded * 100) / e.total);
        setUploadProgress(percent);
      }
    });

    xhr.onload = () => {
      setUploading(false);
      if (xhr.status === 200) {
        const data = JSON.parse(xhr.responseText);
        setStatusMessage(data.status);
        setUploadSuccess(true);
      } else {
        alert("Upload failed");
      }
    };

    xhr.onerror = () => {
      setUploading(false);
      alert("Failed to connect to backend");
    };

    xhr.open("POST", "https://intelliface.outworx.com/api/upload_video");
    xhr.send(formData);
  };

  const handleProcessVideo = async () => {
    setProcessing(true);
    setProcessProgress(0);
    setProcessResult(null);

    let fakeProgress = 0;
    const interval = setInterval(() => {
      fakeProgress += Math.floor(Math.random() * 4) + 1;
      if (fakeProgress >= 95) fakeProgress = 95;
      setProcessProgress(fakeProgress);
    }, 100);

    try {
      const response = await fetch("https://intelliface.outworx.com/api/process_video", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ filename: file.name })
      });
      const data = await response.json();
      setFaceInfo(data.face_info);
      setProcessResult("Video processing completed");
    } catch (error) {
      setProcessResult("Error processing video.");
    } finally {
      clearInterval(interval);
      setProcessProgress(100);
      setProcessing(false);
    }
  };

  const handleShowResults = async () => {
    try {
      const response = await fetch("https://intelliface.outworx.com/api/show_results");
      const data = await response.json();
      setImagePath(data.face_path);

      if (faceInfo?.details?.length > 0) {
        const timeToSeconds = (t) => {
          const [h, m, s] = t.split(":").map(Number);
          return h * 3600 + m * 60 + s;
        };

        const sortedDetails = [...faceInfo.details].sort(
          (a, b) => timeToSeconds(a.timestamp) - timeToSeconds(b.timestamp)
        );

        const sortedImageNames = sortedDetails.map((item) => item.filename);
        setImageNames(sortedImageNames);
      } else {
        setImageNames(data.images);
      }
    } catch (error) {
      alert("Error fetching results");
    }
  };

  const downloadAllImages = () => {
    window.location.href = "https://intelliface.outworx.com/api/download_all_faces";
  };

  const openModal = (idx) => {
    const imgName = imageNames[idx];
    const info = faceInfo?.details?.find((i) => i.filename === imgName);
    setModalImage(`https://intelliface.outworx.com/${imagePath}/${imgName}`);
    setModalInfo(info);
    setModalIndex(idx);
    setModalOpen(true);
  };

  const closeModal = () => setModalOpen(false);
  const nextImage = () => openModal((modalIndex + 1) % imageNames.length);
  const prevImage = () => openModal((modalIndex - 1 + imageNames.length) % imageNames.length);

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <header className="bg-gray-800 p-4 text-xl font-bold">Face Detection App</header>

      <main className="p-6 space-y-6 max-w-5xl mx-auto">
        <section className="bg-gray-800 p-6 rounded-xl">
          <h2 className="text-2xl mb-4">Upload Your Video</h2>
          <input
            type="file"
            accept="video/*"
            onClick={handleFileClick}
            onChange={handleFileChange}
            disabled={uploading}
          />
          {uploading && (
            <div className="mt-2">
              <div className="w-full bg-gray-600 h-3 rounded">
                <div className="bg-blue-500 h-3 rounded" style={{ width: `${uploadProgress}%` }}></div>
              </div>
              <p className="text-sm mt-1">{uploadProgress}%</p>
            </div>
          )}
          {statusMessage && <p className="mt-2 text-green-400">{statusMessage}</p>}
        </section>

        <section className="bg-gray-800 p-6 rounded-xl">
          <h2 className="text-2xl mb-4">Process the Uploaded Video</h2>
          <button
            onClick={handleProcessVideo}
            disabled={!uploadSuccess || processing}
            className="bg-green-600 px-4 py-2 rounded"
          >
            {processing ? "Processing..." : "Process Video"}
          </button>
          {processResult && <p className="mt-2 text-green-400">{processResult}</p>}
          {processing && (
            <div className="mt-2">
              <div className="w-full bg-gray-600 h-3 rounded">
                <div className="bg-green-500 h-3 rounded" style={{ width: `${processProgress}%` }}></div>
              </div>
              <p className="text-sm mt-1">{processProgress}%</p>
            </div>
          )}
        </section>

        <section className="bg-gray-800 p-6 rounded-xl">
          <div className="flex justify-between mb-4">
            <h2 className="text-2xl">✂️ Extracted Faces ({imageNames.length})</h2>
            <button onClick={downloadAllImages} className="bg-blue-600 px-4 py-2 rounded">
              Download All Images
            </button>
          </div>
          <button onClick={handleShowResults} className="bg-indigo-600 px-4 py-2 rounded mb-4">Show Results</button>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {imageNames.map((imgName, idx) => {
              const info = faceInfo?.details?.find((i) => i.filename === imgName);
              return (
                <div key={idx} className="text-center">
                  <img
                    src={`https://intelliface.outworx.com/${imagePath}/${imgName}`}
                    className="w-40 h-40 object-cover mx-auto cursor-pointer border border-gray-600 rounded"
                    onClick={() => openModal(idx)}
                  />
                  <p className="mt-1 text-sm">
                    Confidence: {info?.confidence?.toFixed(2) || "-"} <br />
                    Time: {info?.timestamp || "-"}
                  </p>
                </div>
              );
            })}
          </div>
        </section>

        {modalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50">
            <div className="bg-white text-black p-6 rounded-xl relative max-w-xl w-full">
              <button onClick={closeModal} className="absolute top-3 right-3 text-2xl">&times;</button>
              <img src={modalImage} className="mx-auto max-h-[400px] object-contain" alt="Modal Face" />
              <div className="mt-4 text-center">
                <p>Confidence: {modalInfo?.confidence?.toFixed(2) || "-"}</p>
                <p>Detected at: {modalInfo?.timestamp || "-"}</p>
                <a
                  href={`https://intelliface.outworx.com/api/download_face/${modalImage.split("/").pop()}`}
                  className="mt-2 inline-block bg-blue-600 px-4 py-2 rounded text-white"
                >
                  Download Image
                </a>
              </div>
              <div className="flex justify-between mt-4">
                <button onClick={prevImage} className="px-4 py-2 bg-gray-300 rounded">Prev</button>
                <button onClick={nextImage} className="px-4 py-2 bg-gray-300 rounded">Next</button>
              </div>
            </div>
          </div>
        )}

      </main>
    </div>
  );
}

export default function App() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<UploadPage />} />
      </Routes>
    </Router>
  );
}





